import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Search, User, Loader2, Building2, FolderOpen, Users, Info } from 'lucide-react';
import {
  deliverySearchService,
  EngagementSearchResponse,
  AccountSearchResult,
  ProjectSearchResult,
  ProjectTeamSearchResult,
  EmployeeSearchResult,
  AssignmentSelection,
  MembersPreview,
} from '../services/deliverySearchService';

interface AssignToModalProps {
  assignments: AssignmentSelection[];
  onSave: (assignments: AssignmentSelection[]) => void;
  onClose: () => void;
}

interface PreviewState {
  scope: string;
  id: number;
  data: MembersPreview | null;
  loading: boolean;
}

const SECTION_CAP = 20;

const AssignToModal: React.FC<AssignToModalProps> = ({ assignments: initialAssignments, onSave, onClose }) => {
  const [selections, setSelections] = useState<AssignmentSelection[]>(initialAssignments);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<EngagementSearchResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noResults, setNoResults] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewState | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults(null);
      setNoResults(false);
      setPreviewData(null);
      return;
    }

    const timer = setTimeout(async () => {
      setIsLoading(true);
      setError(null);
      setNoResults(false);
      setPreviewData(null);

      try {
        const response = await deliverySearchService.search(searchQuery);
        setSearchResults(response);
        const hasAny =
          response.accounts.length > 0 ||
          response.projects.length > 0 ||
          response.projectTeams.length > 0 ||
          response.employees.length > 0;
        setNoResults(!hasAny);
      } catch {
        setError('Unable to reach search service. Please try again.');
        setSearchResults(null);
      } finally {
        setIsLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const isAccountSelected = useCallback(
    (acct: AccountSearchResult) =>
      selections.some(s => s.scope === 'account' && s.id === acct.accountId),
    [selections],
  );

  const isProjectSelected = useCallback(
    (proj: ProjectSearchResult) =>
      selections.some(s => s.scope === 'project' && s.id === proj.projectId),
    [selections],
  );

  const isProjectTeamSelected = useCallback(
    (team: ProjectTeamSearchResult) =>
      selections.some(s => s.scope === 'team' && s.id === team.projectTeamId),
    [selections],
  );

  const isEmployeeSelected = useCallback(
    (emp: EmployeeSearchResult) =>
      selections.some(s => s.scope === 'employee' && s.id === emp.employeeId),
    [selections],
  );

  const addSelection = useCallback(
    (selection: AssignmentSelection) => {
      if (selections.some(s => s.scope === selection.scope && s.id === selection.id)) return;
      if (selections.length >= 50) {
        setError('Maximum of 50 assignment selections allowed.');
        return;
      }
      setSelections(prev => [...prev, selection]);
      setError(null);
    },
    [selections],
  );

  const handleSelectAccount = useCallback(
    (acct: AccountSearchResult) => {
      addSelection({ scope: 'account', id: acct.accountId, name: acct.accountName });
    },
    [addSelection],
  );

  const handleSelectProject = useCallback(
    (proj: ProjectSearchResult) => {
      addSelection({
        scope: 'project',
        id: proj.projectId,
        name: proj.projectName,
        accountName: proj.accountName,
      });
    },
    [addSelection],
  );

  const handleSelectProjectTeam = useCallback(
    (team: ProjectTeamSearchResult) => {
      addSelection({
        scope: 'team',
        id: team.projectTeamId,
        name: team.teamName,
        accountName: team.accountName,
        projectName: team.projectName,
      });
    },
    [addSelection],
  );

  const handleSelectEmployee = useCallback(
    (emp: EmployeeSearchResult) => {
      addSelection({
        scope: 'employee',
        id: emp.employeeId,
        name: emp.employeeName,
        accountName: emp.accountName,
        projectName: emp.projectName,
        teamName: emp.teamName,
        teamRole: emp.teamRole,
      });
    },
    [addSelection],
  );

  const handlePreview = useCallback(
    async (e: React.MouseEvent, scope: string, id: number) => {
      e.stopPropagation();

      // Toggle off if same item already open
      if (previewData && previewData.scope === scope && previewData.id === id) {
        setPreviewData(null);
        return;
      }

      setPreviewData({ scope, id, data: null, loading: true });
      try {
        const data = await deliverySearchService.previewMembers(scope, id);
        setPreviewData({ scope, id, data, loading: false });
      } catch {
        setPreviewData({ scope, id, data: null, loading: false });
      }
    },
    [previewData],
  );

  const handleRemove = useCallback((index: number) => {
    setSelections(prev => prev.filter((_, idx) => idx !== index));
  }, []);

  const handleSave = () => {
    if (selections.length === 0) return;
    onSave(selections);
  };

  const isPreviewOpen = (scope: string, id: number) =>
    previewData !== null && previewData.scope === scope && previewData.id === id;

  const hasResults =
    searchResults !== null &&
    (searchResults.accounts.length > 0 ||
      searchResults.projects.length > 0 ||
      searchResults.projectTeams.length > 0 ||
      searchResults.employees.length > 0);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
      <div className="relative top-10 mx-auto p-6 border w-[640px] max-h-[80vh] shadow-lg rounded-lg bg-white dark:bg-gray-800 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Assign To</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search accounts, projects, teams, or people..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {isLoading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-500 animate-spin" />
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/* No results */}
        {noResults && !isLoading && (
          <div className="mb-3 p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm text-gray-500 dark:text-gray-400 text-center">
            No results found
          </div>
        )}

        {/* Search results — grouped sections */}
        {hasResults && (
          <div className="mb-3 max-h-56 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md">

            {/* Accounts */}
            {searchResults!.accounts.slice(0, SECTION_CAP).length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-600 sticky top-0">
                  <Building2 className="h-3 w-3 text-gray-400" />
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Accounts</span>
                </div>
                {searchResults!.accounts.slice(0, SECTION_CAP).map((acct) => {
                  const selected = isAccountSelected(acct);
                  const previewing = isPreviewOpen('account', acct.accountId);
                  return (
                    <div key={`account-${acct.accountId}`} className="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                      <button
                        onClick={() => handleSelectAccount(acct)}
                        disabled={selected}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 ${
                          selected ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-700' : ''
                        }`}
                      >
                        <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {acct.accountName}
                          </div>
                          {acct.engagementTypes.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {acct.engagementTypes.map(et => (
                                <span key={et} className="inline-block px-1.5 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300">
                                  {et}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        {selected && (
                          <span className="text-xs text-green-600 dark:text-green-400 font-medium flex-shrink-0">Added</span>
                        )}
                        <button
                          onClick={(e) => handlePreview(e, 'account', acct.accountId)}
                          className={`p-1 rounded flex-shrink-0 ${previewing ? 'text-blue-500' : 'text-gray-400 hover:text-blue-500'}`}
                          title="Preview members"
                        >
                          <Info className="h-3.5 w-3.5" />
                        </button>
                      </button>
                      {previewing && (
                        <MemberPreviewPopover state={previewData!} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Projects */}
            {searchResults!.projects.slice(0, SECTION_CAP).length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-600 sticky top-0">
                  <FolderOpen className="h-3 w-3 text-gray-400" />
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Projects</span>
                </div>
                {searchResults!.projects.slice(0, SECTION_CAP).map((proj) => {
                  const selected = isProjectSelected(proj);
                  const previewing = isPreviewOpen('project', proj.projectId);
                  return (
                    <div key={`project-${proj.projectId}`} className="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                      <button
                        onClick={() => handleSelectProject(proj)}
                        disabled={selected}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 ${
                          selected ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-700' : ''
                        }`}
                      >
                        <FolderOpen className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {proj.projectName}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {proj.accountName}
                          </div>
                        </div>
                        {selected && (
                          <span className="text-xs text-green-600 dark:text-green-400 font-medium flex-shrink-0">Added</span>
                        )}
                        <button
                          onClick={(e) => handlePreview(e, 'project', proj.projectId)}
                          className={`p-1 rounded flex-shrink-0 ${previewing ? 'text-blue-500' : 'text-gray-400 hover:text-blue-500'}`}
                          title="Preview members"
                        >
                          <Info className="h-3.5 w-3.5" />
                        </button>
                      </button>
                      {previewing && (
                        <MemberPreviewPopover state={previewData!} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Project Teams */}
            {searchResults!.projectTeams.slice(0, SECTION_CAP).length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-600 sticky top-0">
                  <Users className="h-3 w-3 text-gray-400" />
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Project Teams</span>
                </div>
                {searchResults!.projectTeams.slice(0, SECTION_CAP).map((team) => {
                  const selected = isProjectTeamSelected(team);
                  const previewing = isPreviewOpen('team', team.projectTeamId);
                  return (
                    <div key={`team-${team.projectTeamId}`} className="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                      <button
                        onClick={() => handleSelectProjectTeam(team)}
                        disabled={selected}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 ${
                          selected ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-700' : ''
                        }`}
                      >
                        <Users className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {team.teamName}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {team.accountName} › {team.projectName} · {team.memberCount} members
                          </div>
                        </div>
                        {selected && (
                          <span className="text-xs text-green-600 dark:text-green-400 font-medium flex-shrink-0">Added</span>
                        )}
                        <button
                          onClick={(e) => handlePreview(e, 'team', team.projectTeamId)}
                          className={`p-1 rounded flex-shrink-0 ${previewing ? 'text-blue-500' : 'text-gray-400 hover:text-blue-500'}`}
                          title="Preview members"
                        >
                          <Info className="h-3.5 w-3.5" />
                        </button>
                      </button>
                      {previewing && (
                        <MemberPreviewPopover state={previewData!} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Employees */}
            {searchResults!.employees.slice(0, SECTION_CAP).length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-600 sticky top-0">
                  <User className="h-3 w-3 text-gray-400" />
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Employees</span>
                </div>
                {searchResults!.employees.slice(0, SECTION_CAP).map((emp) => {
                  const selected = isEmployeeSelected(emp);
                  return (
                    <button
                      key={`employee-${emp.employeeId}`}
                      onClick={() => handleSelectEmployee(emp)}
                      disabled={selected}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                        selected ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-700' : ''
                      }`}
                    >
                      <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {emp.employeeName}{emp.teamRole ? ` · ${emp.teamRole}` : ''}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {emp.accountName} › {emp.projectName} › {emp.teamName}
                        </div>
                      </div>
                      {selected && (
                        <span className="text-xs text-green-600 dark:text-green-400 font-medium flex-shrink-0">Added</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Selections */}
        <div className="flex-1 min-h-0 overflow-y-auto mb-4">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Selected ({selections.length}/50)
          </div>
          {selections.length === 0 ? (
            <div className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
              No assignments selected. Search and select above.
            </div>
          ) : (
            <div className="space-y-1">
              {selections.map((selection, index) => {
                const Icon =
                  selection.scope === 'account'
                    ? Building2
                    : selection.scope === 'project'
                    ? FolderOpen
                    : User;
                const secondaryText =
                  selection.scope === 'project'
                    ? selection.accountName
                    : selection.scope === 'employee'
                    ? [selection.accountName, selection.projectName, selection.teamName]
                        .filter(Boolean)
                        .join(' › ')
                    : undefined;
                return (
                  <div
                    key={`${selection.scope}-${selection.id}-${index}`}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-md"
                  >
                    <Icon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-gray-900 dark:text-white truncate block">{selection.name}</span>
                      {secondaryText && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 truncate block">{secondaryText}</span>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 capitalize flex-shrink-0">{selection.scope}</span>
                    <button
                      onClick={() => handleRemove(index)}
                      className="text-gray-400 hover:text-red-500 flex-shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={selections.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Assignments
          </button>
        </div>
      </div>
    </div>
  );
};

interface MemberPreviewPopoverProps {
  state: PreviewState;
}

const MemberPreviewPopover: React.FC<MemberPreviewPopoverProps> = ({ state }) => {
  return (
    <div className="mx-3 mb-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md text-sm">
      {state.loading && (
        <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span className="text-xs">Loading members...</span>
        </div>
      )}
      {!state.loading && state.data === null && (
        <p className="text-xs text-red-600 dark:text-red-400">Failed to load members.</p>
      )}
      {!state.loading && state.data !== null && (
        <>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
              {state.data.totalMembers} member{state.data.totalMembers !== 1 ? 's' : ''}
            </span>
            {state.data.missingEmails > 0 && (
              <span className="text-xs text-amber-600 dark:text-amber-400">
                {state.data.missingEmails} missing email{state.data.missingEmails !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {state.data.members.map((member) => (
              <div key={member.employeeId} className="flex items-center justify-between gap-2">
                <span className="text-xs text-gray-800 dark:text-gray-200 truncate">{member.employeeName}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400 truncate flex-shrink-0">{member.email}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default AssignToModal;
